import React, { Dispatch, SetStateAction, useRef, useState } from 'react';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  ChakraBox,
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuList,
  ChakraText,
  EquipUITheme,
  FAIconName,
  FireflyButton,
  TableColumn,
} from '@equip.health/ui';

import { TABLE_DEFAULT_COLUMNS } from '~/lib/constants/patients.constants';

import PatientColumnItem from './PatientColumnItem';

interface PatientFiltersProps {
  headerMapFromTableHeads: Record<string, TableColumn<PatientRow>>;
  onFilterChange: Dispatch<SetStateAction<string[]>>;
  onSortChange: Dispatch<SetStateAction<string[]>>;
  selectedFilter: string[];
  selectedSortOrder: string[];
}

const { h6 } = EquipUITheme.typography;

const TOP_LEVEL_ID = 'patient-column-filter';

const PatientColumnFilters = ({
  headerMapFromTableHeads,
  onFilterChange,
  onSortChange,
  selectedFilter,
  selectedSortOrder,
}: PatientFiltersProps) => {
  const [resetFilter, setResetFilter] = useState<boolean>(true);
  const [filterState, setFilterState] = useState<string[]>([]);

  const [columnListHeaderKeyString, setColumnListHeaderKeyString] = useState<
    string[]
  >([]);

  const bodyStylesRef = useRef<{
    left: string;
    position: string;
    right: string;
    top: string;
  }>(null);

  const onMenuClose = () => {
    setResetFilter(true);
    if (bodyStylesRef.current) {
      const { left, position, right, top } = bodyStylesRef.current;
      document.body.style.left = left;
      document.body.style.position = position;
      document.body.style.right = right;
      document.body.style.top = top;
    }
  };

  const onMenuOpen = () => {
    if (resetFilter) {
      setColumnListHeaderKeyString(selectedSortOrder ?? []);
      setFilterState(selectedFilter ?? []);
      setResetFilter(false);
    }

    bodyStylesRef.current = {
      left: document.body.style.left,
      position: document.body.style.position,
      right: document.body.style.right,
      top: document.body.style.top,
    };

    document.body.style.left = '0';
    document.body.style.position = 'fixed';
    document.body.style.right = '0';
    document.body.style.top = '0';
  };

  const onSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    if (event.target.checked) {
      setFilterState([...(filterState ?? []), key]);
    } else {
      setFilterState(filterState.filter((filter) => filter !== key));
    }
  };

  const onDragEnd = (event: DragEndEvent): string[] | void => {
    const { active: oldElement, over: newElement } = event;

    if (oldElement?.id !== newElement?.id) {
      setColumnListHeaderKeyString((items: string[]): string[] => {
        const oldIndex = items.indexOf(oldElement?.id as string);
        const newIndex = items.indexOf(newElement?.id as string);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const SortableColumn = ({
    id,
    keyId,
    text,
  }: {
    id: string;
    keyId: string;
    text: string;
  }) => {
    const { setNodeRef, transform, transition, listeners } = useSortable({
      id,
    });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <ChakraBox ref={setNodeRef} style={style}>
        <PatientColumnItem
          filterState={filterState}
          keyId={keyId}
          listeners={listeners}
          onSwitchChange={onSwitchChange}
          text={text}
        />
      </ChakraBox>
    );
  };

  return (
    <ChakraMenu
      closeOnBlur
      id={`${TOP_LEVEL_ID}__menu`}
      onClose={onMenuClose}
      onOpen={onMenuOpen}
      placement="bottom-end"
    >
      {({ onClose }) => (
        <>
          <ChakraMenuButton
            as={FireflyButton}
            id={`${TOP_LEVEL_ID}__menu-button`}
            leftIcon={FAIconName.gearRegular}
            size="small"
            variant="secondary"
          >
            Customize table
          </ChakraMenuButton>
          <ChakraMenuList
            id={`${TOP_LEVEL_ID}__menu-list`}
            maxWidth="360px"
            padding="20px"
            width="360px"
            zIndex={100}
          >
            <ChakraText {...h6} fontWeight="600">
              Columns
            </ChakraText>
            <ChakraBox
              marginY="16px"
              maxHeight="calc(70vh - 100px)"
              overflowY="auto"
            >
              {TABLE_DEFAULT_COLUMNS.map((key: string) => {
                return (
                  <PatientColumnItem
                    filterState={filterState}
                    key={key}
                    keyId={key}
                    onSwitchChange={onSwitchChange}
                    text={(headerMapFromTableHeads[key] ?? {})?.header ?? ''}
                  />
                );
              })}
              <DndContext onDragEnd={onDragEnd}>
                <SortableContext
                  items={columnListHeaderKeyString ?? []}
                  strategy={verticalListSortingStrategy}
                >
                  {(columnListHeaderKeyString ?? []).map((key: string) => {
                    if (TABLE_DEFAULT_COLUMNS.includes(key)) return null;
                    return (
                      <SortableColumn
                        id={key}
                        key={key}
                        keyId={key}
                        text={
                          (headerMapFromTableHeads[key] ?? {})?.header ?? ''
                        }
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </ChakraBox>
            <ChakraBox
              background="neutral.line"
              height="1px"
              marginBottom="16px"
              width="100%"
            />
            <Button
              id={`${TOP_LEVEL_ID}__apply`}
              onClick={() => {
                onClose();
                onFilterChange(filterState);
                onSortChange(columnListHeaderKeyString ?? []);
                setResetFilter(false);
              }}
              width="100%"
            >
              Apply
            </Button>
          </ChakraMenuList>
        </>
      )}
    </ChakraMenu>
  );
};

export default PatientColumnFilters;
